import { useState, type FormEvent } from 'react'
import { Loader2, Send } from 'lucide-react'
import { Modal, Field } from '@/shared/ui/Modal'
import { Note } from '@/shared/ui/states'
import { ApiError, explain } from '@/shared/api/problem'
import { sendEmail } from './api'

/**
 * Ad-hoc send. Guarded by `notification:send`.
 *
 * The endpoint returns 202 with a message id — the send is queued, not completed,
 * so the copy says "queued" rather than "sent". Claiming delivery here and then
 * showing FAILED in the log a second later is worse than saying less.
 */
export function SendEmailDialog({
  open, onClose, onSent,
}: { open: boolean; onClose: () => void; onSent: () => void }) {
  const [recipient, setRecipient] = useState('')
  const [subject, setSubject] = useState('')
  const [bodyHtml, setBodyHtml] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  function reset() {
    setRecipient(''); setSubject(''); setBodyHtml('')
    setError(null); setFieldErrors({})
  }

  async function submit(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    setFieldErrors({})
    try {
      await sendEmail({ recipient: recipient.trim(), subject: subject.trim(), bodyHtml })
      reset()
      onSent()
      onClose()
    } catch (err) {
      // Server field errors map back onto the fields by name.
      if (err instanceof ApiError) setFieldErrors(err.fieldErrors())
      setError(explain(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={() => { reset(); onClose() }}
      title="Send an email"
      description="Queued through the same pipeline as system messages, and logged the same way."
      footer={
        <>
          <button type="button" className="erp-btn" onClick={() => { reset(); onClose() }}>
            Cancel
          </button>
          <button
            type="submit"
            form="send-email-form"
            className="erp-btn erp-btn-primary"
            disabled={busy || !recipient.trim() || !subject.trim() || !bodyHtml.trim()}
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
            Queue message
          </button>
        </>
      }
    >
      <form id="send-email-form" onSubmit={submit} className="space-y-4">
        {error && <Note tone="bad">{error}</Note>}

        <Field label="Recipient" required error={fieldErrors.recipient}>
          <input
            type="email"
            value={recipient}
            onChange={(e) => setRecipient(e.target.value)}
            placeholder="name@example.com"
            className="erp-input w-full"
            required
          />
        </Field>

        <Field label="Subject" required error={fieldErrors.subject}>
          <input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className="erp-input w-full"
            maxLength={255}
            required
          />
        </Field>

        <Field
          label="Body (HTML)"
          required
          hint="Stored and shown in the log as source. Keep it simple — most mail clients ignore anything clever."
          error={fieldErrors.bodyHtml}
        >
          <textarea
            value={bodyHtml}
            onChange={(e) => setBodyHtml(e.target.value)}
            rows={8}
            className="erp-input w-full !h-auto py-2 font-mono text-erp-xs resize-y"
            placeholder="<p>Hello,</p>"
            required
          />
        </Field>
      </form>
    </Modal>
  )
}
